import React from "react";
import type { Product } from "../../types";
import { useCart } from "../../hooks/use-cart";
import clickSound from "../../../../assets/audio/click.wav";
import trashSound from "../../../../assets/audio/trash.wav";
import { useAudioEffects } from "../../../../hooks/use-audio-effects";

interface Props {
  product: Product;
  className?: string;
  quantityStep?: number;
  hideQuantity?: boolean;
  onClick?: () => void;
}

const CartButton = ({
  product,
  className = "",
  quantityStep = 1,
  hideQuantity = false,
  onClick = () => {},
}: Props) => {
  const { items, addToCart, removeFromCart } = useCart();
  const { playEffect } = useAudioEffects();

  if (!product) {
    return (
      <button
        disabled
        className={`px-3 py-1 rounded-sm bg-gray-800 text-xs ${className}`}
      >
        No product
      </button>
    );
  }

  const cartItem = items.find((it) => it.productId === product.id);
  const existsInCart = !!cartItem;
  const qty = cartItem?.quantity ?? 0;

  const handleAdd = () => {
    onClick();
    playEffect(clickSound);
    addToCart(
      product.id,
      product.previewImage + "",
      quantityStep,
      product.name,
      product.price
    );
  };
  const handleRemove = () => {
    onClick();
    playEffect(trashSound);
    removeFromCart(product.id);
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {qty > 0 && !hideQuantity && (
        <button
          aria-label="remove from cart"
          onClick={handleRemove}
          className="px-2 py-1 rounded bg-red-600 text-white text-sm"
          title="Remove one"
        >
          −
        </button>
      )}

      {!hideQuantity && qty > 0 && (
        <div className="text-sm min-w-[28px] text-center">{qty}</div>
      )}

      <button
        type="button"
        title="Cart"
        onClick={existsInCart ? handleRemove : handleAdd}
        className={`bg-gradient-to-r w-full block rounded-md py-1 relative overflow-hidden transition hover:opacity-80 active:scale-95 ${
          existsInCart
            ? "from-[#56F5FF]/10 via-[#56F5FF]/40 to-[#56F5FF]/10"
            : "from-white/5 via-white/30 to-white/5"
        }`}
      >
        <p className="text-white text-[15px]">
          {existsInCart ? "Remove from Cart" : "Add to Cart"}
        </p>

        {/* Borders */}
        <div
          className={`absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r ${
            existsInCart
              ? "from-[#56F5FF]/20 via-[#56F5FF]/60 to-[#56F5FF]/20"
              : "from-white/5 via-white/40 to-white/5"
          }`}
        ></div>
        <div
          className={`absolute top-0 left-0 h-full w-[1px] ${
            existsInCart ? "bg-[#56F5FF]/10" : "bg-white/5"
          }`}
        ></div>
        <div
          className={`absolute top-0 right-0 h-full w-[1px] ${
            existsInCart ? "bg-[#56F5FF]/10" : "bg-white/5"
          }`}
        ></div>
        <div
          className={`absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r ${
            existsInCart
              ? "from-[#56F5FF]/20 via-[#56F5FF]/60 to-[#56F5FF]/20"
              : "from-white/5 via-white/40 to-white/5"
          }`}
        ></div>
      </button>

      {qty > 0 && !hideQuantity && (
        <button
          aria-label="add to cart"
          onClick={handleAdd}
          className="px-2 py-1 rounded bg-green-600 text-white text-sm"
          title="Add one"
        >
          +
        </button>
      )}
    </div>
  );
};

export default CartButton;
